import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Sparkles, Check } from "lucide-react";

/** Nine emotional/sensory statements — YUMO infers Taste DNA from taps. */
const STATEMENTS = [
  { key: "spicy",       label: "I enjoy spicy flavours",      emoji: "🌶" },
  { key: "mild",        label: "I prefer mild flavours",       emoji: "🌱" },
  { key: "comforting",  label: "I love comforting meals",      emoji: "🍲" },
  { key: "tangy",       label: "I enjoy tangy food",           emoji: "🍋" },
  { key: "sweet",       label: "I enjoy sweet dishes",         emoji: "🍯" },
  { key: "crunchy",     label: "I prefer crunchy textures",    emoji: "🌰" },
  { key: "creamy",      label: "I enjoy creamy textures",      emoji: "🥛" },
  { key: "light",       label: "I prefer light meals",         emoji: "🥗" },
  { key: "rich",        label: "I enjoy rich flavours",        emoji: "🧈" },
];

const DIET_OPTIONS = [
  { key: "vegetarian", label: "Vegetarian" },
  { key: "non-vegetarian", label: "Non-vegetarian" },
  { key: "eggitarian", label: "Includes eggs" },
  { key: "vegan", label: "Vegan" },
];

export default function Onboarding() {
  const { refreshUser } = useAuth();
  const nav = useNavigate();
  const [picked, setPicked] = useState([]);
  const [diet, setDiet] = useState("non-vegetarian");
  const [step, setStep] = useState(0);            // 0 = statements, 1 = diet
  const [submitting, setSubmitting] = useState(false);

  const toggle = (k) => setPicked((p) => p.includes(k) ? p.filter(x => x !== k) : [...p, k]);

  const finish = async () => {
    setSubmitting(true);
    try {
      // Silently detect device context — no user input needed
      const locale = typeof navigator !== "undefined" ? navigator.language : null;
      const timezone = typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone : null;
      await api.post("/onboarding", {
        taste_statements: picked,
        diet_type: diet,
        locale,
        timezone,
      });
      try { await refreshUser(); } catch (e) { console.warn(e?.message); }
      toast.success("Your Taste DNA is ready");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't finish setup — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-14" data-testid="onboarding-page">
      <div className="overline mb-3">Your Taste DNA · under a minute</div>

      {step === 0 && (
        <>
          <h1 className="font-serif text-4xl md:text-5xl font-semibold tracking-tight leading-[1.05]">
            Tap the flavours that <span className="italic text-primary">feel like you</span>.
          </h1>
          <p className="mt-4 text-muted-foreground">Pick as many as you like. Skip the rest. YUMO figures out the details.</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-8">
            {STATEMENTS.map((s) => {
              const active = picked.includes(s.key);
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => toggle(s.key)}
                  data-testid={`taste-stmt-${s.key}`}
                  className={`relative p-5 rounded-2xl border text-left transition ${
                    active ? "bg-primary/10 border-primary text-foreground"
                           : "bg-card border-border hover:border-foreground/30"
                  }`}
                >
                  <div className="text-2xl mb-2">{s.emoji}</div>
                  <div className="text-sm font-medium">{s.label}</div>
                  {active && <Check className="absolute top-3 right-3 w-4 h-4 text-primary" strokeWidth={2} />}
                </button>
              );
            })}
          </div>

          <div className="mt-8 flex items-center justify-between">
            <div className="text-sm text-muted-foreground">{picked.length} selected</div>
            <Button
              onClick={() => setStep(1)}
              disabled={picked.length === 0}
              className="rounded-full h-11 px-6"
              data-testid="onboarding-next"
            >
              Continue <Sparkles className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </>
      )}

      {step === 1 && (
        <>
          <h1 className="font-serif text-4xl md:text-5xl font-semibold tracking-tight leading-[1.05]">
            One last thing — <span className="italic text-primary">what do you eat</span>?
          </h1>
          <p className="mt-4 text-muted-foreground">The only question we truly can't guess.</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-8">
            {DIET_OPTIONS.map((d) => (
              <button
                key={d.key}
                type="button"
                onClick={() => setDiet(d.key)}
                data-testid={`diet-${d.key}`}
                className={`p-5 rounded-2xl border text-left transition ${
                  diet === d.key ? "bg-primary/10 border-primary" : "bg-card border-border hover:border-foreground/30"
                }`}
              >
                <div className="font-medium">{d.label}</div>
              </button>
            ))}
          </div>

          <Card className="mt-8 p-4 rounded-2xl border-primary/20 bg-primary/[0.03] text-sm">
            YUMO is quietly noting your timezone, language, and recent context. No forms. No city dropdowns.
          </Card>

          <div className="mt-6 flex items-center justify-between">
            <Button variant="ghost" onClick={() => setStep(0)} className="rounded-full" data-testid="onboarding-back">
              Back
            </Button>
            <Button
              onClick={finish}
              disabled={submitting}
              className="rounded-full h-11 px-8"
              data-testid="onboarding-finish"
            >
              {submitting ? "Setting up…" : "Start using YUMO"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
